import { useMemo } from 'react'

export interface LazyContentResult {
  readonly displayContent: string
  readonly shouldShowMore: boolean
  readonly totalLines: number
}

export interface LazyContentOptions {
  readonly content: string
  readonly expanded: boolean
  readonly previewThreshold?: number
}

export interface LazyEditContentResult {
  readonly displayOldString: string
  readonly displayNewString: string
  readonly shouldShowMore: boolean
  readonly totalLines: number
}

export interface LazyEditContentOptions {
  readonly oldString: string
  readonly newString: string
  readonly expanded: boolean
  readonly previewThreshold?: number
}

export function useLazyContent({
  content,
  expanded,
  previewThreshold = 30,
}: LazyContentOptions): LazyContentResult {
  const totalLines = useMemo(() => content.split('\n').length, [content])

  const shouldShowMore = useMemo(
    () => totalLines > previewThreshold,
    [totalLines, previewThreshold],
  )

  const displayContent = useMemo(() => {
    if (!shouldShowMore || expanded) {
      return content
    }

    const lines = content.split('\n')
    return lines.slice(0, previewThreshold).join('\n')
  }, [content, shouldShowMore, expanded, previewThreshold])

  return {
    displayContent,
    shouldShowMore,
    totalLines,
  }
}

export function useLazyEditContent({
  oldString,
  newString,
  expanded,
  previewThreshold = 30,
}: LazyEditContentOptions): LazyEditContentResult {
  const totalLines = useMemo(
    () => Math.max(oldString.split('\n').length, newString.split('\n').length),
    [oldString, newString],
  )

  const shouldShowMore = useMemo(
    () => totalLines > previewThreshold,
    [totalLines, previewThreshold],
  )

  const displayOldString = useMemo(() => {
    if (!shouldShowMore || expanded) {
      return oldString
    }

    const lines = oldString.split('\n')
    return lines.slice(0, previewThreshold).join('\n')
  }, [oldString, shouldShowMore, expanded, previewThreshold])

  const displayNewString = useMemo(() => {
    if (!shouldShowMore || expanded) {
      return newString
    }

    const lines = newString.split('\n')
    return lines.slice(0, previewThreshold).join('\n')
  }, [newString, shouldShowMore, expanded, previewThreshold])

  return {
    displayOldString,
    displayNewString,
    totalLines,
    shouldShowMore,
  }
}
